import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePickerInput } from '@/components/ui/date-picker-input';
import { Loader2, ClipboardCheck } from 'lucide-react';
import { format, startOfDay } from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';
import { QUALIFICATION_OBJECTIVES, URGENCY_OPTIONS } from '@/lib/qualificationOptions';
import { cn } from '@/lib/utils';

interface QualificationRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    urgency?: string;
    objective?: string;
    reuniao_data?: Date;
  }) => void;
  missingFields: string[];
  isLoading?: boolean;
}

export function QualificationRequiredModal({
  isOpen,
  onClose,
  onConfirm,
  missingFields,
  isLoading = false,
}: QualificationRequiredModalProps) {
  const { t, i18n } = useTranslation();
  
  const [urgency, setUrgency] = useState('');
  const [objective, setObjective] = useState('');
  const [reuniaoData, setReuniaoData] = useState<Date | undefined>();
  const [calendarOpen, setCalendarOpen] = useState(false);

  const dateLocale = i18n.language === 'pt-BR' ? ptBR : i18n.language === 'es' ? es : enUS;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setUrgency('');
      setObjective('');
      setReuniaoData(undefined);
    }
  }, [isOpen]);

  const needsUrgency = missingFields.includes('urgency');
  const needsObjective = missingFields.includes('objective');
  const needsMeetingDate = missingFields.includes('reuniao_data');

  const handleConfirm = () => {
    const data: { urgency?: string; objective?: string; reuniao_data?: Date } = {};
    
    if (needsUrgency && urgency) data.urgency = urgency;
    if (needsObjective && objective) data.objective = objective;
    if (needsMeetingDate && reuniaoData) data.reuniao_data = reuniaoData;
    
    onConfirm(data);
  };

  const handleClose = () => {
    onClose();
  };

  const isValid = 
    (!needsUrgency || urgency) && 
    (!needsObjective || objective) && 
    (!needsMeetingDate || reuniaoData);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <ClipboardCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <DialogTitle>{t('stageTransition.qualificationTitle')}</DialogTitle>
              <DialogDescription>
                {t('stageTransition.qualificationDescription')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {needsUrgency && (
            <div className="space-y-2">
              <Label htmlFor="urgency">{t('stageTransition.urgency')} *</Label>
              <Select value={urgency} onValueChange={setUrgency}>
                <SelectTrigger id="urgency">
                  <SelectValue placeholder={t('common.selectOption')} />
                </SelectTrigger>
                <SelectContent>
                  {URGENCY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {needsObjective && (
            <div className="space-y-2">
              <Label htmlFor="objective">{t('stageTransition.objective')} *</Label>
              <Select value={objective} onValueChange={setObjective}>
                <SelectTrigger id="objective">
                  <SelectValue placeholder={t('common.selectOption')} />
                </SelectTrigger>
                <SelectContent>
                  {QUALIFICATION_OBJECTIVES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {needsMeetingDate && (
            <div className="space-y-2">
              <Label>{t('stageTransition.meetingDate')} *</Label>
              <DatePickerInput
                value={reuniaoData ?? null}
                onChange={(date) => setReuniaoData(date ?? undefined)}
                minDate={startOfDay(new Date())}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              t('stageTransition.scheduleMeeting')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default QualificationRequiredModal;
